"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { Menu, X, Sprout, UserRound, SlidersHorizontal } from "lucide-react";
import GardenNavLink from "./GardenNavLink";
import LanguageSwitch from "./LanguageSwitch";
import GardenThemeToggle from "./GardenThemeToggle";

export default function NavBar() {
  const pathname = usePathname() || "/";
  const isEn = pathname === "/en" || pathname.startsWith("/en/");
  const menu = useRef<HTMLDetailsElement>(null);
  const gardenHref = isEn ? "/en/garden" : "/garden";
  const aboutHref = isEn ? "/en/about" : "/about";
  const inGarden =
    pathname === gardenHref || pathname.startsWith(`${gardenHref}/`);
  const closeMenu = () => menu.current?.removeAttribute("open");
  useEffect(() => {
    menu.current?.removeAttribute("open");
  }, [pathname]);

  return (
    <header className="site-header">
      <div className="site-header-inner">
        <Link href={isEn ? "/en" : "/"} className="site-brand">
          Lukiluki
        </Link>
        <nav
          className="site-desktop-nav"
          aria-label={isEn ? "Main navigation" : "Hauptnavigation"}
        >
          <GardenNavLink isEn={isEn} />
          <Link
            href={aboutHref}
            prefetch={false}
            aria-current={pathname === aboutHref ? "page" : undefined}
            className="site-about-link"
          >
            {isEn ? "About" : "Über mich"}
          </Link>
        </nav>
        <div className="site-desktop-controls">
          <GardenThemeToggle />
          <LanguageSwitch />
        </div>
        <details
          ref={menu}
          className="site-mobile-menu"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              closeMenu();
              menu.current?.querySelector<HTMLElement>("summary")?.focus();
            }
          }}
        >
          <summary
            className="site-menu-trigger"
            aria-label={
              isEn ? "Open or close menu" : "Menü öffnen oder schließen"
            }
          >
            <Menu
              className="site-menu-open-icon"
              size={19}
              aria-hidden="true"
            />
            <X className="site-menu-close-icon" size={19} aria-hidden="true" />
            <span>{isEn ? "Menu" : "Menü"}</span>
          </summary>
          <div
            className="site-mobile-panel"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("a")) closeMenu();
            }}
          >
            <nav
              aria-label={isEn ? "Main navigation" : "Hauptnavigation"}
              className="site-mobile-links"
            >
              <Link
                href={gardenHref}
                prefetch={false}
                aria-current={inGarden ? "page" : undefined}
              >
                <Sprout size={21} aria-hidden="true" />
                <span>
                  <strong>Digital Garden</strong>
                  <small>
                    {isEn
                      ? "Notes & interactive playgrounds"
                      : "Notizen & interaktive Playgrounds"}
                  </small>
                </span>
              </Link>
              <Link
                href={aboutHref}
                prefetch={false}
                aria-current={pathname === aboutHref ? "page" : undefined}
              >
                <UserRound size={21} aria-hidden="true" />
                <span>
                  <strong>{isEn ? "About" : "Über mich"}</strong>
                  <small>
                    {isEn
                      ? "Person, experience & projects"
                      : "Person, Erfahrung & Projekte"}
                  </small>
                </span>
              </Link>
            </nav>
            <section
              className="site-mobile-ambience"
              aria-label={isEn ? "Ambience settings" : "Ambiente-Einstellungen"}
            >
              <p className="site-menu-label">
                <SlidersHorizontal size={13} aria-hidden="true" />
                {isEn ? "Ambience" : "Ambiente"}
              </p>
              <GardenThemeToggle embedded />
            </section>
            <div className="site-mobile-language">
              <span className="site-menu-label">
                {isEn ? "Language" : "Sprache"}
              </span>
              <LanguageSwitch />
            </div>
            <div className="site-mobile-legal pt-3 border-t border-black/5 dark:border-white/5 text-center">
              <Link
                href={isEn ? "/en/impressum" : "/impressum"}
                prefetch={false}
                className="text-xs text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300"
              >
                {isEn ? "Legal Notice & Privacy" : "Impressum & Datenschutz"}
              </Link>
            </div>
          </div>
        </details>
      </div>
    </header>
  );
}
